import clsx from 'clsx';
import { Children, cloneElement, forwardRef, isValidElement, PropsWithChildren, ReactElement } from 'react';
import { ButtonProps } from '../button';
import { IconButtonProps } from '../icon-button';
import { Stack, StackProps } from '../stack';
import classes from './button-group.module.scss';

export type ButtonGroupProps = PropsWithChildren &
  StackProps & Pick<ButtonProps | IconButtonProps, 'color' | 'variant'>;

/**
 * `ButtonGroup` is a functional component designed to render a group of buttons, which can share props and styles for unified formatting.
 *
 * It utilizes a `Stack` internally to manage layout and spacing. The component processes its children to apply additional properties
 * passed through `ButtonGroupProps` while preserving the individual props of the child components.
 *
 * Features:
 * - Combines multiple buttons or icon buttons into a cohesive group.
 * - Applies consistent spacing and layout alignment across the child components.
 * - Allows the `variant` property to influence button styling.
 *
 * Props:
 * - Inherits all properties from `ButtonGroupProps`.
 *
 * Note:
 * - Child elements must be valid React elements and typically include button or icon button components.
 */
export const ButtonGroup = forwardRef<HTMLDivElement, ButtonGroupProps>(({
  children,
  variant,
  color,
  ...props
}, ref) => {
  return (
    <Stack
      {...props}
      ref={ref}
      as={'nav'}
      role="region"
      spacing={0}
      className={clsx(classes.root, props.className, { [classes.border]: variant !== 'outlined' })}
      crossAxis="stretch"
    >
      {Children.map(
        children as ReactElement<Omit<ButtonProps | IconButtonProps, 'color'>[]>,
        child => {
          if (!isValidElement(child)) {
            return null;
          }
          return cloneElement(child, {
            ...{ variant, color },
            ...child.props
          });
        }
      )}
    </Stack>
  );
});